import { useCallback, useMemo } from "react";
import {
  Budget,
  OffHoursSchedule2,
  Schedule2,
  useGetUsersMeOrganisationsCurrentBudgetsQuery,
  useGetV2UsersMeOffHoursSchedulesQuery,
} from "../../../../../services/cloudchipr.api";
import {
  UsageDataType,
  UsagesDataType,
  UsageType,
} from "../types/integration-usage";
import { useAppSelector } from "../../../../../store/hooks";
import { schedulesDataSelector } from "../../../../../store/schedules/selectors/schedule/schedulesDataSelector";

export function useIntegrationsUsage() {
  const { data: offHours } = useGetV2UsersMeOffHoursSchedulesQuery({});
  const { data: budgets } = useGetUsersMeOrganisationsCurrentBudgetsQuery();
  const scheduledJobs = useAppSelector(schedulesDataSelector);

  const integrationsUsage = useMemo(() => {
    if (!offHours && !scheduledJobs && !budgets) {
      return {};
    }

    const offHoursData = generateIntegrationUsageData(
      offHours || [],
      {},
      "offHours",
    );

    const schedulesData = generateIntegrationUsageData(
      scheduledJobs || [],
      offHoursData,
      "schedule",
    );

    return generateIntegrationUsageData(budgets || [], schedulesData, "budget");
  }, [offHours, scheduledJobs, budgets]);

  const getIntegrationsUsage = useCallback(
    (id: string): UsagesDataType => {
      const usages = (integrationsUsage[id] || []) as UsageDataType[];

      return usages.reduce(
        (acc, usage) => {
          acc[usage.type].push(usage);

          return acc;
        },
        {
          schedule: [] as UsageDataType[],
          offHours: [] as UsageDataType[],
          budget: [] as UsageDataType[],
        },
      );
    },
    [integrationsUsage],
  );

  const getIntegrationsUsageExist = useCallback(
    (id: string): boolean => !!integrationsUsage[id]?.length,
    [integrationsUsage],
  );

  return {
    getIntegrationsUsageExist,
    getIntegrationsUsage,
  };
}

const generateIntegrationUsageData = (
  sources: (Schedule2 | OffHoursSchedule2 | Budget)[],
  initialData: Record<string, Record<string, string>[]>,
  type: UsageType,
): UsagesDataType => {
  return sources.reduce((acc, source, index) => {
    const integrations = [...(source?.notifications ?? [])];

    if (isWorkflow(source)) {
      source.grace_period?.notifications?.forEach((notification) => {
        if (notification?.integration_id) {
          integrations.push({
            ...notification,
            id: notification.integration_id,
          });
        }
      });
    }

    integrations?.forEach(({ id }: { id: string }, i) => {
      const data = {
        uniqueKey: source.id + index + i,
        name: source.name,
        id: source.id,
        type,
      };

      if (acc[id]) {
        acc[id].push(data);
      } else {
        acc[id] = [data];
      }
    });

    return acc;
  }, initialData) as UsagesDataType;
};

const isWorkflow = (source: any): source is Schedule2 => {
  return !!source?.grace_period;
};
